import { Integration } from "@/types/general";
import BaseButton from "@reusables/BaseButton";
import { Alert } from "@mui/material";
import { useTranslation } from "react-i18next";
import { ArrowLeft } from "lucide-react";

interface ReimportConfirmationProps {
    integrationName: string;
    riskyEntities: Integration.Import.Entity[];
    safeEntities: Integration.Import.Entity[];
    onBack: () => void;
    onConfirm: () => void;
    isLoading?: boolean;
}

export default function ReimportConfirmation({
    integrationName,
    riskyEntities,
    safeEntities,
    onBack,
    onConfirm,
    isLoading,
}: ReimportConfirmationProps) {
    const { t } = useTranslation("", { keyPrefix: "imports.reimport.confirmation" });
    const entityT = useTranslation("", { keyPrefix: "imports.reimport.entities" }).t;

    /**
     * Rendering translated entity names as a warning-friendly list while inheriting the alert text color.
     */
    const renderEntityList = (entities: Integration.Import.Entity[]) => (
        <ul className="mt-1 list-disc space-y-1 pl-5">
            {entities.map((entity) => (
                <li key={entity}>{entityT(entity)}</li>
            ))}
        </ul>
    );

    return (
        <div className="space-y-6">
            <div className="space-y-2 text-center">
                <div className="text-xl font-semibold text-accent">
                    {t("heading", { integrationName })}
                </div>
                <div className="text-secondary-gray">{t("description")}</div>
            </div>

            <Alert severity="warning">
                <div className="space-y-4">
                    <div>
                        <div className="font-semibold">{t("riskyEntitiesLabel")}</div>
                        {renderEntityList(riskyEntities)}
                    </div>
                    {safeEntities.length > 0 && (
                        <div>
                            <div className="font-semibold">{t("safeEntitiesLabel")}</div>
                            {renderEntityList(safeEntities)}
                        </div>
                    )}
                </div>
            </Alert>

            <div className="grid grid-cols-2 gap-2">
                <BaseButton
                    buttonWidth="100%"
                    text={t("buttons.back")}
                    size="md"
                    type="button"
                    icon={<ArrowLeft size={18} strokeWidth={2.25} />}
                    iconPos="left"
                    primaryOutlined
                    onClick={onBack}
                    disabled={isLoading}
                />
                <BaseButton
                    buttonWidth="100%"
                    text={t("buttons.confirm")}
                    size="md"
                    type="button"
                    onClick={onConfirm}
                    loading={isLoading}
                />
            </div>
        </div>
    );
}
